/**
 * FERP Portal - Quick Add Task Modal
 *
 * Lightweight modal for rapid task capture with multi-modal input:
 * - Text: Direct keyboard entry
 * - Voice: Web Speech API (on-device when available)
 * - Photo: Camera capture → OCR → text extraction
 * - Document: File upload → OCR → text extraction
 *
 * Keyboard activation: Cmd+. (registered in MainLayout)
 * Context-aware: Pre-fills subjectActorId from FocusContext
 *
 * Processing: Edge hub (T7920) for local LLM when available,
 * fallback to cloud Presidio + Claude.
 *
 * FERP Principles:
 * - DRR-114: T4 media processed on-device, never uploaded
 * - DRR-128: Write-time minimization — only structured text leaves device
 * - Dumb Frontend: Uses backend classification for entity extraction
 * - Repository pattern: Uses CreateTask mutation
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';

// Web Speech API types (not in default TypeScript lib)
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionResultList {
  length: number;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  [index: number]: SpeechRecognitionAlternative;
  isFinal: boolean;
  length: number;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

declare global {
  interface Window {
    SpeechRecognition?: new () => SpeechRecognition;
    webkitSpeechRecognition?: new () => SpeechRecognition;
  }
}
import {
  Modal,
  TextInput,
  Textarea,
  Select,
  Button,
  Group,
  Stack,
  Alert,
  Collapse,
  ActionIcon,
  Text,
  LoadingOverlay,
  Tooltip,
  Badge,
  Divider,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import {
  IconPlus,
  IconChevronDown,
  IconChevronUp,
  IconFocus,
  IconAlertCircle,
  IconCamera,
  IconMicrophone,
  IconMicrophoneOff,
  IconFile,
  IconLoader,
  IconVideo,
  IconPlayerStop,
  IconX,
} from '@tabler/icons-react';
import { useScanToFill } from '@ferp/workflow-forms';
import { useIntakeProcess, IntakeProposal, IntakeExtractedField } from '@/hooks/useIntakeProcess';
import { notifications } from '@mantine/notifications';
import { useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useTenantId } from '@/auth/AuthContext';
import { useMyActor } from '@/hooks/useMyActor';
import { useFocusContext } from '@/contexts/FocusContext';
import { usePersonalInboxWorkflow } from '@/hooks/usePersonalInboxWorkflow';
import { GqlTaskPriority } from '@/generated/graphql';

// Direct gql until codegen syncs with backend
const CREATE_TASK = gql`
  mutation CreateTask($input: GqlCreateTaskInput!) {
    taskApiCreateTask(input: $input) {
      id
      name
      status
    }
  }
`;

const GENERATE_UPLOAD_URL = gql`
  mutation GenerateDocumentUploadUrl($input: GenerateDocumentUploadUrlInput!) {
    documentApiGenerateDocumentUploadUrl(input: $input) {
      uploadUrl
      documentId
    }
  }
`;

interface UploadUrlResult {
  documentApiGenerateDocumentUploadUrl?: {
    uploadUrl: string;
    documentId: string;
  };
}

interface CreateTaskResult {
  taskApiCreateTask?: {
    id: string;
    name: string;
    status: string;
  };
}

interface QuickAddTaskModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess?: (taskId: string) => void;
}

interface TaskFormValues {
  name: string;
  description: string;
  priority: GqlTaskPriority;
  dueAt: string | null;
}

const PRIORITY_OPTIONS = [
  { value: GqlTaskPriority.LOW, label: 'Low' },
  { value: GqlTaskPriority.NORMAL, label: 'Normal' },
  { value: GqlTaskPriority.HIGH, label: 'High' },
  { value: GqlTaskPriority.CRITICAL, label: 'Critical' },
];

// Spin animation for loading icon
const spinStyle = `
@keyframes ferp-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.spin { animation: ferp-spin 1s linear infinite; }
`;

// Voice recognition hook using Web Speech API + audio recording
function useVoiceInput() {
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [supported, setSupported] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
    setSupported(!!SpeechRecognitionAPI);

    if (SpeechRecognitionAPI) {
      const recognition = new SpeechRecognitionAPI();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        let finalTranscript = '';
        let interimTranscript = '';
        for (let i = 0; i < event.results.length; i++) {
          const result = event.results[i];
          if (result?.isFinal) {
            finalTranscript += result[0]?.transcript + ' ';
          } else if (result?.[0]) {
            interimTranscript = result[0].transcript;
          }
        }
        setTranscript((finalTranscript + interimTranscript).trim());
      };

      recognition.onerror = () => setListening(false);

      recognitionRef.current = recognition;
    }

    return () => {
      recognitionRef.current?.abort();
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const startListening = useCallback(async () => {
    if (!listening) {
      setTranscript('');
      setAudioBlob(null);
      audioChunksRef.current = [];

      // Start speech recognition
      recognitionRef.current?.start();

      // Start audio recording
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;
        const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) audioChunksRef.current.push(e.data);
        };

        recorder.onstop = () => {
          const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          setAudioBlob(blob);
          stream.getTracks().forEach((t) => t.stop());
        };

        mediaRecorderRef.current = recorder;
        recorder.start(1000); // Collect chunks every second
      } catch {
        console.warn('Audio recording not available, using speech-to-text only');
      }

      setListening(true);
    }
  }, [listening]);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setListening(false);
  }, []);

  const toggleListening = useCallback(() => {
    if (listening) stopListening();
    else startListening();
  }, [listening, startListening, stopListening]);

  const clearAudio = useCallback(() => setAudioBlob(null), []);

  return { listening, transcript, audioBlob, supported, toggleListening, startListening, stopListening, clearAudio };
}

export function QuickAddTaskModal({ opened, onClose, onSuccess }: QuickAddTaskModalProps) {
  const tenantId = useTenantId();
  const { actorId: myActorId } = useMyActor();

  // Fallback to dev impersonation actor if getMyActor query fails
  // This handles mock auth mode where auth_user_id doesn't match any actor
  const devActorId = typeof window !== 'undefined' ? localStorage.getItem('dev_actor_auth_id') : null;
  const actorId = myActorId || devActorId || '2_1'; // Default to first Hillside actor

  const { focusedSubject, focusedDisplayName, isFocused } = useFocusContext();
  const { workflowInstanceId, loading: inboxLoading, error: inboxError } = usePersonalInboxWorkflow(actorId);

  // Multi-modal input - same pipeline as AgentAssistPanel
  const { processImage, scanning, ocrAvailable, statusMessage } = useScanToFill();
  const { process: agentProcess, loading: agentProcessing } = useIntakeProcess();
  const { listening, transcript, audioBlob, supported: voiceSupported, toggleListening, clearAudio } = useVoiceInput();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const [showMore, setShowMore] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [captureMode, setCaptureMode] = useState<'text' | 'voice' | 'photo' | 'video' | 'doc' | null>(null);
  const [formValues, setFormValues] = useState<TaskFormValues>({
    name: '',
    description: '',
    priority: GqlTaskPriority.NORMAL,
    dueAt: null,
  });

  // Apply voice transcript to task name
  useEffect(() => {
    if (transcript && captureMode === 'voice') {
      setFormValues((prev) => ({ ...prev, name: transcript }));
    }
  }, [transcript, captureMode]);

  // Handle image capture (camera or file) - same flow as AgentAssistPanel
  const handleImageCapture = useCallback(async (file: File) => {
    try {
      // Step 1: On-device OCR (T4 stays on device)
      const scanResult = await processImage(file);

      if (scanResult) {
        const ocrText = Object.values(scanResult).join(' ');

        // Step 2: Send to agent for classification (text pseudonymized by hub)
        const proposals = await agentProcess({
          prompt: 'Extract a task name and description from this document for quick capture.',
          mediaText: ocrText,
        });

        // Use agent response or fallback to raw OCR
        const taskProposal = proposals?.find((p: IntakeProposal) => p.target === 'task') ?? proposals?.[0];
        if (taskProposal) {
          const nameField = taskProposal.extractedFields.find((f: IntakeExtractedField) => f.fieldName === 'name');
          const descField = taskProposal.extractedFields.find((f: IntakeExtractedField) => f.fieldName === 'description');

          setFormValues((prev) => ({
            ...prev,
            name: (nameField?.value as string) || prev.name,
            description: (descField?.value as string) || ocrText.slice(0, 500) || prev.description,
          }));
          notifications.show({
            title: 'Document processed',
            message: `Extracted: "${(nameField?.value as string)?.slice(0, 40) || 'task details'}..."`,
            color: 'green',
          });
        } else {
          // Fallback: use raw OCR text
          setFormValues((prev) => ({
            ...prev,
            name: prev.name || ocrText.slice(0, 80),
            description: ocrText,
          }));
          notifications.show({
            title: 'Text extracted',
            message: `${ocrText.slice(0, 40)}...`,
            color: 'blue',
          });
        }
        setShowMore(true);
      } else {
        notifications.show({
          title: 'No text detected',
          message: 'Try a clearer image or type manually',
          color: 'yellow',
        });
        setFormValues((prev) => ({
          ...prev,
          name: prev.name || 'Photo note',
          description: prev.description || `Captured ${new Date().toLocaleString()}`,
        }));
      }
    } catch (err) {
      console.error('Image processing failed:', err);
      notifications.show({
        title: 'Processing failed',
        message: 'Try voice mode or type manually',
        color: 'red',
      });
    }
    setCaptureMode(null);
  }, [processImage, agentProcess]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCaptureMode('doc');
      handleImageCapture(file);
    }
    e.target.value = ''; // Reset for re-selection
  }, [handleImageCapture]);

  const handleCameraChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCaptureMode('photo');
      handleImageCapture(file);
    }
    e.target.value = '';
  }, [handleImageCapture]);

  // WebRTC camera for desktop
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);

  // Video recording
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      setCameraStream(stream);
      setCameraActive(true);
      setCaptureMode('photo');
      // srcObject wired via useEffect after video element renders
    } catch (err) {
      console.error('Camera access denied:', err);
      // Fallback to file picker
      cameraInputRef.current?.click();
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    setCameraActive(false);
    setCaptureMode(null);
  }, [cameraStream]);

  const capturePhoto = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0);

    canvas.toBlob(async (blob) => {
      if (blob) {
        stopCamera();
        await handleImageCapture(new File([blob], 'capture.jpg', { type: 'image/jpeg' }));
      }
    }, 'image/jpeg', 0.9);
  }, [stopCamera, handleImageCapture]);

  // Wire up video element when camera becomes active
  useEffect(() => {
    if (cameraActive && cameraStream && videoRef.current) {
      videoRef.current.srcObject = cameraStream;
      videoRef.current.play().catch((err) => console.error('Video play failed:', err));
    }
  }, [cameraActive, cameraStream]);

  // Cleanup camera on close
  useEffect(() => {
    if (!opened && cameraStream) {
      stopCamera();
    }
  }, [opened, cameraStream, stopCamera]);

  const handleCameraClick = useCallback(() => {
    if (isMobile) {
      // Mobile: use native camera input
      cameraInputRef.current?.click();
    } else {
      // Desktop: use WebRTC
      startCamera();
    }
  }, [isMobile, startCamera]);

  // Video recording with audio
  const startVideoRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });
      setCameraStream(stream);
      setCameraActive(true);
      setCaptureMode('video');

      // Wait for video element to be ready
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(console.error);
        }

        // Setup MediaRecorder
        const mediaRecorder = new MediaRecorder(stream, { mimeType: 'video/webm;codecs=vp9,opus' });
        mediaRecorderRef.current = mediaRecorder;
        recordedChunksRef.current = [];

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            recordedChunksRef.current.push(event.data);
          }
        };

        mediaRecorder.onstop = () => {
          const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
          setRecordedBlob(blob);
        };

        mediaRecorder.start();
        setIsRecording(true);
      }, 100);
    } catch (err) {
      console.error('Video recording denied:', err);
      notifications.show({
        title: 'Camera access denied',
        message: 'Please allow camera and microphone access',
        color: 'red',
      });
    }
  }, []);

  const stopVideoRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, [isRecording]);

  const handleVideoSubmit = useCallback(async () => {
    if (!recordedBlob) return;

    stopCamera();

    try {
      // Try to get transcription via agent (routes to T7920 hub)
      const proposals = await agentProcess({
        prompt: 'Transcribe this video/audio recording and extract a task name and description.',
        mediaText: '[Video recording - audio transcription requested]',
      });

      const taskProposal = proposals?.find((p: IntakeProposal) => p.target === 'task') ?? proposals?.[0];
      if (taskProposal) {
        const nameField = taskProposal.extractedFields.find((f: IntakeExtractedField) => f.fieldName === 'name');
        const descField = taskProposal.extractedFields.find((f: IntakeExtractedField) => f.fieldName === 'description');

        setFormValues((prev) => ({
          ...prev,
          name: (nameField?.value as string) || prev.name,
          description: (descField?.value as string) || prev.description,
        }));
        notifications.show({
          title: 'Recording processed',
          message: 'Task details extracted',
          color: 'green',
        });
      } else {
        // Fallback: duration-based placeholder
        const audioContext = new AudioContext();
        const arrayBuffer = await recordedBlob.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        const duration = Math.round(audioBuffer.duration);

        setFormValues((prev) => ({
          ...prev,
          name: prev.name || `Recording (${duration}s)`,
          description: prev.description || `Recorded ${new Date().toLocaleString()}`,
        }));
        notifications.show({
          title: 'Video captured',
          message: `${duration}s recorded. Use voice mode for real-time transcription.`,
          color: 'blue',
        });
      }
      setShowMore(true);
    } catch (err) {
      console.error('Video processing failed:', err);
      setFormValues((prev) => ({
        ...prev,
        name: prev.name || 'Video note',
        description: prev.description || `Recorded ${new Date().toLocaleString()}`,
      }));
    }

    setRecordedBlob(null);
    setCaptureMode(null);
  }, [recordedBlob, stopCamera, agentProcess]);

  const [createTask] = useMutation<CreateTaskResult>(CREATE_TASK, {
    refetchQueries: ['GetTasks', 'GetMyTasks', 'GetTasksByActor'],
  });

  const [generateUploadUrl] = useMutation<UploadUrlResult>(GENERATE_UPLOAD_URL);

  // Reset form when modal opens
  useEffect(() => {
    if (opened) {
      setFormValues({
        name: '',
        description: '',
        priority: GqlTaskPriority.NORMAL,
        dueAt: null,
      });
      setShowMore(false);
    }
  }, [opened]);

  const handleSubmit = useCallback(async () => {
    if (!formValues.name.trim()) {
      notifications.show({
        title: 'Task name required',
        message: 'Please enter a name for the task',
        color: 'red',
      });
      return;
    }

    if (!workflowInstanceId) {
      notifications.show({
        title: 'Personal inbox not ready',
        message: 'Please wait for your inbox to be created',
        color: 'red',
      });
      return;
    }

    setSubmitting(true);

    try {
      const result = await createTask({
        variables: {
          input: {
            workflowInstanceId,
            name: formValues.name.trim(),
            description: formValues.description.trim() || formValues.name.trim(),
            taskType: 'manual',
            priority: formValues.priority,
            assignedActorId: actorId,
            subjectActorId: focusedSubject || undefined,
            dueAt: formValues.dueAt || undefined,
          },
        },
      });

      const taskId = result.data?.taskApiCreateTask?.id;

      // Upload voice recording if captured
      if (taskId && audioBlob) {
        try {
          const uploadResult = await generateUploadUrl({
            variables: {
              input: {
                fileName: `voice-note-${Date.now()}.webm`,
                contentType: 'audio/webm',
                entityType: 'task',
                entityId: taskId,
              },
            },
          });

          const uploadUrl = uploadResult.data?.documentApiGenerateDocumentUploadUrl?.uploadUrl;
          if (uploadUrl) {
            await fetch(uploadUrl, {
              method: 'PUT',
              body: audioBlob,
              headers: { 'Content-Type': 'audio/webm' },
            });
          }
        } catch (uploadErr) {
          console.warn('Voice note upload failed:', uploadErr);
        }
        clearAudio();
      }

      notifications.show({
        title: 'Task created',
        message: `"${formValues.name}" added to your inbox${audioBlob ? ' (with voice note)' : ''}`,
        color: 'green',
      });

      if (taskId) {
        onSuccess?.(taskId);
      }
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Failed to create task',
        message: error instanceof Error ? error.message : 'An error occurred',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  }, [formValues, workflowInstanceId, actorId, focusedSubject, createTask, audioBlob, generateUploadUrl, clearAudio, onSuccess, onClose]);

  // Handle Enter key in name field
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey && formValues.name.trim()) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [formValues.name, handleSubmit]
  );

  const isReady = !inboxLoading && !inboxError && workflowInstanceId;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Quick Add Task"
      size="md"
      centered
      overlayProps={{ backgroundOpacity: 0.3, blur: 3 }}
    >
      <style>{spinStyle}</style>
      <LoadingOverlay visible={submitting} />

      <Stack gap="md">
        {/* Context banner if focused on a subject */}
        {isFocused && focusedDisplayName && (
          <Alert color="blue" icon={<IconFocus size={16} />} p="xs">
            <Text size="sm">Task will be associated with: <strong>{focusedDisplayName}</strong></Text>
          </Alert>
        )}

        {/* Error state */}
        {inboxError && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            <Text size="sm">Could not create personal inbox: {inboxError.message}</Text>
          </Alert>
        )}

        {/* Debug: Show why Add Task might be disabled */}
        {!isReady && !inboxError && (
          <Alert color="yellow" icon={<IconAlertCircle size={16} />} p="xs">
            <Text size="xs">
              {inboxLoading ? 'Loading inbox...' : !workflowInstanceId ? 'Creating inbox...' : 'Unknown'}
            </Text>
          </Alert>
        )}

        {/* Hidden file inputs */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.pdf,.doc,.docx"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={handleCameraChange}
        />

        {/* Capture mode buttons */}
        <Group gap="xs" justify="center">
          <Tooltip label={voiceSupported ? 'Voice input' : 'Voice not supported'}>
            <ActionIcon
              variant={listening ? 'filled' : 'light'}
              color={listening ? 'red' : 'blue'}
              size="lg"
              onClick={() => {
                setCaptureMode('voice');
                toggleListening();
              }}
              disabled={!voiceSupported || scanning}
            >
              {listening ? <IconMicrophoneOff size={20} /> : <IconMicrophone size={20} />}
            </ActionIcon>
          </Tooltip>

          <Tooltip label={ocrAvailable ? 'Photo (on-device OCR)' : 'Photo capture'}>
            <ActionIcon
              variant="light"
              color="teal"
              size="lg"
              onClick={handleCameraClick}
              disabled={scanning || cameraActive}
            >
              {scanning && captureMode === 'photo' ? <IconLoader size={20} className="spin" /> : <IconCamera size={20} />}
            </ActionIcon>
          </Tooltip>

          <Tooltip label="Record video + audio">
            <ActionIcon
              variant={isRecording ? 'filled' : 'light'}
              color={isRecording ? 'red' : 'orange'}
              size="lg"
              onClick={isRecording ? stopVideoRecording : startVideoRecording}
              disabled={scanning || (cameraActive && !isRecording)}
            >
              {isRecording ? <IconPlayerStop size={20} /> : <IconVideo size={20} />}
            </ActionIcon>
          </Tooltip>

          <Tooltip label="Upload document">
            <ActionIcon
              variant="light"
              color="grape"
              size="lg"
              onClick={() => fileInputRef.current?.click()}
              disabled={scanning}
            >
              {scanning && captureMode === 'doc' ? <IconLoader size={20} className="spin" /> : <IconFile size={20} />}
            </ActionIcon>
          </Tooltip>
        </Group>

        {/* Camera/Video preview (desktop WebRTC) */}
        {cameraActive && (
          <Stack gap="xs">
            <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', background: '#000' }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted={captureMode !== 'video'}
                style={{ width: '100%', maxHeight: 300, objectFit: 'cover' }}
              />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
              {isRecording && (
                <div style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  background: 'red',
                  borderRadius: '50%',
                  width: 12,
                  height: 12,
                  animation: 'ferp-spin 1s ease-in-out infinite',
                }} />
              )}
            </div>
            {captureMode === 'photo' && (
              <Group justify="center" gap="sm">
                <Button
                  color="teal"
                  leftSection={<IconCamera size={16} />}
                  onClick={capturePhoto}
                >
                  Capture
                </Button>
                <Button
                  variant="subtle"
                  color="gray"
                  onClick={stopCamera}
                >
                  Cancel
                </Button>
              </Group>
            )}
            {captureMode === 'video' && isRecording && (
              <Group justify="center" gap="sm">
                <Button
                  color="red"
                  leftSection={<IconPlayerStop size={16} />}
                  onClick={stopVideoRecording}
                >
                  Stop Recording
                </Button>
              </Group>
            )}
          </Stack>
        )}

        {/* Status message for OCR processing */}
        {statusMessage && (
          <Text size="xs" c="dimmed" ta="center">
            {statusMessage}
          </Text>
        )}

        {listening && (
          <Badge color="red" variant="dot" fullWidth>
            Listening... speak your task (recording audio)
          </Badge>
        )}

        {/* Audio playback after voice recording */}
        {audioBlob && !listening && (
          <Stack gap="xs">
            <Group justify="space-between" align="center">
              <Text size="sm" c="dimmed">Voice recording captured</Text>
              <ActionIcon variant="subtle" color="gray" size="sm" onClick={clearAudio}>
                <IconX size={14} />
              </ActionIcon>
            </Group>
            <audio controls src={URL.createObjectURL(audioBlob)} style={{ width: '100%', height: 32 }} />
          </Stack>
        )}

        {isRecording && (
          <Badge color="red" variant="dot" fullWidth>
            Recording video... click stop when done
          </Badge>
        )}

        {recordedBlob && !isRecording && (
          <Group justify="center" gap="sm">
            <Button color="teal" onClick={handleVideoSubmit} loading={scanning}>
              Process Recording
            </Button>
            <Button variant="subtle" color="gray" onClick={() => { setRecordedBlob(null); stopCamera(); }}>
              Discard
            </Button>
          </Group>
        )}

        <Divider />

        {/* Main input */}
        <TextInput
          label="Task name"
          placeholder={listening ? 'Speak now...' : 'What needs to be done?'}
          required
          autoFocus
          value={formValues.name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormValues((prev) => ({ ...prev, name: e.target.value }))}
          onKeyDown={handleKeyDown}
          disabled={!isReady || scanning}
        />

        {/* More options toggle */}
        <Group justify="flex-start">
          <ActionIcon
            variant="subtle"
            onClick={() => setShowMore((prev) => !prev)}
            aria-label={showMore ? 'Hide options' : 'Show more options'}
          >
            {showMore ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
          </ActionIcon>
          <Text size="sm" c="dimmed" style={{ cursor: 'pointer' }} onClick={() => setShowMore((prev) => !prev)}>
            {showMore ? 'Hide options' : 'More options'}
          </Text>
        </Group>

        {/* Expanded options */}
        <Collapse in={showMore}>
          <Stack gap="sm">
            <Textarea
              label="Description"
              placeholder="Additional details (optional)"
              value={formValues.description}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormValues((prev) => ({ ...prev, description: e.target.value }))}
              minRows={2}
              maxRows={4}
            />

            <Group grow>
              <Select
                label="Priority"
                data={PRIORITY_OPTIONS}
                value={formValues.priority}
                onChange={(value: string | null) =>
                  setFormValues((prev) => ({
                    ...prev,
                    priority: (value as GqlTaskPriority) || GqlTaskPriority.NORMAL,
                  }))
                }
              />

              <DateTimePicker
                label="Due date"
                placeholder="Optional"
                value={formValues.dueAt ? new Date(formValues.dueAt) : null}
                onChange={(value) => {
                  // DateTimePicker returns Date | null in Mantine v8
                  const isoString = value ? new Date(value).toISOString() : null;
                  setFormValues((prev) => ({ ...prev, dueAt: isoString }));
                }}
                clearable
                minDate={new Date()}
              />
            </Group>
          </Stack>
        </Collapse>

        {/* Actions */}
        <Group justify="flex-end" mt="sm">
          <Button variant="subtle" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleSubmit}
            loading={submitting}
            disabled={!isReady || !formValues.name.trim()}
          >
            Add Task
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
